/**
 * Dynamic Settings Filter Utility
 * Provides functions to filter settings based on category, platform, and entry point
 */

import { NativePlatformType, SettingsEntryType } from '../Contracts';
import { logConstants, SettingsLogger } from '../utils/SettingsLogger';
import { SettingsCategory } from './SettingsCategory';
import {
	getSettingConfig as getSettingConfigFromDefinitions,
	SettingConfig,
	SettingType,
	ToggleSettingGroupConfig,
	RadioSettingGroupConfig,
} from './SettingsDefinitions';
import { getSettingIdsFromLookup, SETTINGS_LOOKUP } from './SettingsPlatformConfig';

export interface SettingsFilterCriteria {
	category: SettingsCategory;
	platform?: NativePlatformType;
	entrypoint?: SettingsEntryType;
}

/**
 * Get filtered setting IDs based on category, platform, and entry point
 * Uses the tree structure to efficiently retrieve settings
 * @param criteria Filter criteria
 * @returns Array of setting IDs that match the criteria
 */
export function getFilteredSettingIds(criteria: SettingsFilterCriteria): string[] {
	const { category, platform, entrypoint } = criteria;

	SettingsLogger.getInstance().write(
		logConstants.DEBUG,
		`Filtering settings for category: ${category}, platform: ${platform}, entrypoint: ${entrypoint}`
	);

	// If platform and entrypoint are provided, use tree structure for direct lookup
	if (platform && entrypoint) {
		return getSettingIdsFromLookup(platform, entrypoint, category);
	}

	// If only platform is provided, aggregate settings from all entrypoints
	if (platform && !entrypoint) {
		const allSettings: string[] = [];
		const platformTree = SETTINGS_LOOKUP[platform];
		if (platformTree) {
			for (const entry in platformTree) {
				const settings = getSettingIdsFromLookup(
					platform,
					entry as SettingsEntryType,
					category
				);
				// Add only unique settings
				settings.forEach((settingId: string) => {
					if (!allSettings.includes(settingId)) {
						allSettings.push(settingId);
					}
				});
			}
		}
		return allSettings;
	}

	// If no platform/entrypoint specified, aggregate from all platforms and entrypoints
	const allSettings: string[] = [];
	for (const plat in SETTINGS_LOOKUP) {
		const platformTree = SETTINGS_LOOKUP[plat as NativePlatformType];
		if (!platformTree) continue;
		for (const entry in platformTree) {
			const settings = getSettingIdsFromLookup(
				plat as NativePlatformType,
				entry as SettingsEntryType,
				category
			);
			// Add only unique settings
			settings.forEach((settingId: string) => {
				if (!allSettings.includes(settingId)) {
					allSettings.push(settingId);
				}
			});
		}
	}
	return allSettings;
}

/**
 * Get setting configuration for a given setting ID
 * Uses the flat structure for direct lookup
 * @param settingId The setting ID
 * @returns The setting configuration or undefined if not found
 */
export function getSettingConfig(settingId: string): SettingConfig | undefined {
	return getSettingConfigFromDefinitions(settingId);
}

/**
 * Expand setting IDs for use in getSettings bridge calls.
 * For TOGGLE_SETTING_GROUP and RADIO_SETTING_GROUP parents the parent ID is included
 * first (so the native layer can return its own hidden/enabled state for the card),
 * followed by its sub-setting IDs (so the native layer can return each sub-setting's value).
 * All other IDs are kept as-is.
 * @param settingIds Array of setting IDs (may include group parent IDs)
 * @returns Array of setting IDs with groups expanded into parent ID + sub-setting IDs
 */
export function expandGroupSettingIds(settingIds: string[]): string[] {
	const expanded: string[] = [];
	for (const id of settingIds) {
		const config = getSettingConfigFromDefinitions(id);
		if (config?.type === SettingType.TOGGLE_SETTING_GROUP) {
			const groupConfig = config as ToggleSettingGroupConfig;
			expanded.push(id); // include parent ID for hidden/disabled state
			groupConfig.toggleOptions.forEach((opt) => expanded.push(opt.id));
		} else if (config?.type === SettingType.RADIO_SETTING_GROUP) {
			const groupConfig = config as RadioSettingGroupConfig;
			expanded.push(id); // include parent ID for hidden/disabled state
			groupConfig.radioOptions.forEach((opt) => expanded.push(opt.id));
		} else {
			expanded.push(id);
		}
	}
	return expanded;
}

/**
 * Resolves which original (parent) setting IDs should be visible for rendering,
 * given the set of visible IDs returned by the native layer.
 * For TOGGLE_SETTING_GROUP and RADIO_SETTING_GROUP parents: visible only if the parent ID
 * itself is not hidden in the native response (the parent's own hidden flag controls the card).
 * For all other settings: visible if the setting ID itself is in visibleIds.
 * Falls back to visible if the native layer did not return data for an ID (treated as defaults).
 * @param filteredIds Original setting IDs from the platform lookup (may include group parents)
 * @param visibleIds Set of setting IDs (both parent and sub) that are not hidden in the native layer response
 * @returns Array of setting IDs suitable for rendering
 */
export function resolveVisibleParentIds(
	filteredIds: string[],
	visibleIds: Set<string>
): string[] {
	const result: string[] = [];
	for (const id of filteredIds) {
		const config = getSettingConfigFromDefinitions(id);
		if (
			config?.type === SettingType.TOGGLE_SETTING_GROUP ||
			config?.type === SettingType.RADIO_SETTING_GROUP
		) {
			// The parent card is hidden/shown based on the parent ID's own hidden flag.
			// Sub-setting visibility controls individual items inside the card, not the card itself.
			if (visibleIds.has(id)) {
				result.push(id);
			}
		} else if (visibleIds.has(id)) {
			result.push(id);
		}
	}
	return result;
}

/**
 * Check if a setting is available for a specific platform and entry point
 * @param settingId The setting ID
 * @param platform The platform to check
 * @param entrypoint The entry point to check
 * @returns True if the setting is available, false otherwise
 */
export function isSettingAvailable(
	settingId: string,
	platform?: NativePlatformType,
	entrypoint?: SettingsEntryType
): boolean {
	// If no platform/entrypoint specified, assume available if config exists
	if (!platform || !entrypoint) {
		return getSettingConfigFromDefinitions(settingId) !== undefined;
	}

	// Check if setting exists in the lookup tree for this platform/entrypoint
	const platformTree = SETTINGS_LOOKUP[platform];
	if (!platformTree) {
		return false;
	}

	const entrypointTree = platformTree[entrypoint];
	if (!entrypointTree) {
		return false;
	}

	// Search through all categories in this entrypoint
	for (const category in entrypointTree) {
		const settingIds = entrypointTree[category];
		if (settingIds && settingIds.includes(settingId)) {
			return true;
		}
	}

	return false;
}
